'use client'

import type { NumberFormatValues } from 'react-number-format'

import { useCallback, useMemo } from 'react'

import Button, { ButtonVariant } from '@/components/atomic/button'
import Icon, { IconName } from '@/components/atomic/icon'
import InputV2 from '@/components/atomic/input-v2'
import Slider from '@/components/atomic/slider'
import { BodyXS } from '@/components/atomic/text'
import Toggle from '@/components/atomic/toggle'
import ModelConstraintCard from '@/components/molecules/model-constraint-card'
import { ResetButton } from '@/components/molecules/target-band/styles'
import { formatMoney } from '@/utils/presentation/formatting'

import {
  addTier,
  getDefaultTiers,
  getErrorForTier,
  removeTier,
  validateTierMarketValues,
} from './helpers/validation'
import {
  ModeToggleContainer,
  RowLabel,
  SliderWrapper,
  TierInputContainer,
  TierRow,
  TiersContainer,
} from './styles'
import {
  type DiversificationConstraintValue,
  DiversificationMode,
  type TieredDiversificationTier,
} from './types'

interface DiversificationConstraintProps {
  title: string
  tooltipText?: string
  value: DiversificationConstraintValue | null
  sliderDefaults: {
    defaultValue: [number, number]
    min: number
    max: number
    step: number
  }
  formatter: (value: number) => string
  onValueChange: (value: DiversificationConstraintValue) => void
  onReset: () => void
}

function DiversificationConstraint({
  title,
  tooltipText,
  value,
  sliderDefaults,
  formatter,
  onValueChange,
  onReset,
}: DiversificationConstraintProps) {
  const { defaultValue, min, max, step } = sliderDefaults

  // Derive mode and values from the unified value prop
  const mode = value?.mode ?? DiversificationMode.Default
  const currentWeightRange: [number, number] =
    value?.mode === DiversificationMode.Default
      ? value.weightRange
      : defaultValue
  // Null means no constraint; show the full range until the user sets one
  const displayedWeightRange: [number, number] =
    value === null ? [min, max] : currentWeightRange
  const tiers: TieredDiversificationTier[] =
    value?.mode === DiversificationMode.Tiered
      ? value.tiers
      : getDefaultTiers(defaultValue)

  const handleModeToggle = useCallback(
    (checked: boolean) => {
      const newMode = checked
        ? DiversificationMode.Tiered
        : DiversificationMode.Default

      if (newMode === DiversificationMode.Tiered) {
        // Switch to tiered mode with default tiers
        const newValue: DiversificationConstraintValue = {
          mode: DiversificationMode.Tiered,
          tiers: getDefaultTiers(currentWeightRange),
        }
        onValueChange(newValue)
      } else {
        // Switch to default mode with current weight range or default
        const newValue: DiversificationConstraintValue = {
          mode: DiversificationMode.Default,
          weightRange: defaultValue,
        }
        onValueChange(newValue)
      }
    },
    [onValueChange, currentWeightRange, defaultValue],
  )

  const handleSliderValueChange = useCallback(
    (newWeightRange: number[]) => {
      const newValue: DiversificationConstraintValue = {
        mode: DiversificationMode.Default,
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        weightRange: [newWeightRange[0]!, newWeightRange[1]!],
      }
      onValueChange(newValue)
    },
    [onValueChange],
  )

  const handleAddTier = useCallback(() => {
    if (value?.mode !== DiversificationMode.Tiered) return
    const newValue: DiversificationConstraintValue = {
      mode: DiversificationMode.Tiered,
      tiers: addTier(tiers),
    }
    onValueChange(newValue)
  }, [value, tiers, onValueChange])

  const handleRemoveTier = useCallback(
    (index: number) => {
      if (value?.mode !== DiversificationMode.Tiered) return
      const newValue: DiversificationConstraintValue = {
        mode: DiversificationMode.Tiered,
        tiers: removeTier(tiers, index),
      }
      onValueChange(newValue)
    },
    [value, tiers, onValueChange],
  )

  const handleTierMarketValueChange = useCallback(
    (index: number, values: NumberFormatValues) => {
      if (value?.mode !== DiversificationMode.Tiered) return
      const newTiers = [...tiers]
      newTiers[index] = {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        ...newTiers[index]!,
        maxMarketValue: values.floatValue ?? null,
      }
      const newValue: DiversificationConstraintValue = {
        mode: DiversificationMode.Tiered,
        tiers: newTiers,
      }
      onValueChange(newValue)
    },
    [value, tiers, onValueChange],
  )

  const handleTierWeightChange = useCallback(
    (index: number, weightRange: [number, number]) => {
      if (value?.mode !== DiversificationMode.Tiered) return
      const newTiers = [...tiers]
      newTiers[index] = {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        ...newTiers[index]!,
        weightRange: [weightRange[0], weightRange[1]],
      }
      const newValue: DiversificationConstraintValue = {
        mode: DiversificationMode.Tiered,
        tiers: newTiers,
      }
      onValueChange(newValue)
    },
    [value, tiers, onValueChange],
  )

  // Validation
  const validationResult = useMemo(
    () => validateTierMarketValues(tiers),
    [tiers],
  )

  // Get the "Above" label value for the last tier
  const lastTierLabel = useMemo(() => {
    if (tiers.length < 2) return 'Above'
    const previousTier = tiers[tiers.length - 2]
    // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
    if (previousTier!.maxMarketValue !== null) {
      // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
      return `Above ${formatMoney(previousTier!.maxMarketValue, { numDigits: 0 })}`
    }
    return 'Above'
  }, [tiers])

  const handleReset = useCallback(() => {
    const resetValue: DiversificationConstraintValue = {
      mode: DiversificationMode.Default,
      weightRange: defaultValue,
    }
    onValueChange(resetValue)
    onReset()
  }, [onValueChange, defaultValue, onReset])

  return (
    <ModelConstraintCard
      title={title}
      tooltipText={tooltipText}
      rightHeaderContent={<ResetButton onClick={handleReset} />}
    >
      <ModeToggleContainer>
        <BodyXS className="text-moment-blue-grey-800 dark:text-moment-blue-grey-400">
          Default
        </BodyXS>
        <Toggle
          checked={mode === DiversificationMode.Tiered}
          onChange={handleModeToggle}
        />
        <BodyXS className="text-moment-blue-grey-800 dark:text-moment-blue-grey-400">
          Tiered
        </BodyXS>
      </ModeToggleContainer>

      {mode === DiversificationMode.Default ? (
        <Slider
          defaultValue={displayedWeightRange}
          min={min}
          max={max}
          step={step}
          className="w-full"
          formatter={formatter}
          showMinMaxInputs
          onValueChange={handleSliderValueChange}
        />
      ) : (
        <TiersContainer>
          {tiers.map((tier, index) => {
            const isLastTier = index === tiers.length - 1
            const error = getErrorForTier(validationResult.errors, index)

            return (
              <TierRow key={tier.id}>
                <RowLabel>{isLastTier ? lastTierLabel : 'Up to'}</RowLabel>
                <TierInputContainer>
                  {!isLastTier && (
                    // biome-ignore lint/complexity/noUselessFragments: Bulk suppression as part of biome migration
                    <>
                      <div>
                        <InputV2
                          type="currency"
                          placeholder="$0"
                          value={tier.maxMarketValue ?? undefined}
                          onValueChange={(values) =>
                            handleTierMarketValueChange(index, values)
                          }
                          hasError={!!error}
                          size="small"
                        />
                        {error && (
                          <BodyXS className="text-moment-red-600 mt-1">
                            {error}
                          </BodyXS>
                        )}
                      </div>
                    </>
                  )}
                  <div>
                    <Icon
                      iconName={IconName.ArrowRightIcon}
                      size="small"
                      className="text-moment-blue-grey-400 flex-shrink-0"
                    />
                  </div>
                  <SliderWrapper>
                    <Slider
                      key={tier.id}
                      defaultValue={tier.weightRange}
                      min={min}
                      max={max}
                      step={step}
                      className="w-full"
                      formatter={formatter}
                      showMinMaxInputs
                      onValueChange={(value) =>
                        handleTierWeightChange(index, value as [number, number])
                      }
                    />
                  </SliderWrapper>
                  {!isLastTier && tiers.length > 2 && (
                    <Button
                      variant={ButtonVariant.Ghost}
                      onClick={() => handleRemoveTier(index)}
                      icon={
                        <Icon
                          iconName={IconName.MinusCircleIcon}
                          size="medium"
                          className="text-moment-red-600"
                        />
                      }
                    />
                  )}
                </TierInputContainer>
              </TierRow>
            )
          })}

          <Button
            variant={ButtonVariant.Ghost}
            label="Add another tier"
            iconSide="left"
            icon={<Icon iconName={IconName.PlusIcon} size="medium" />}
            className="px-0 text-moment-blue-grey-400"
            onClick={handleAddTier}
          />
        </TiersContainer>
      )}
    </ModelConstraintCard>
  )
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default DiversificationConstraint
